/* eslint-disable no-underscore-dangle */
import { nanoid } from 'nanoid';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import { dropAt } from '../../../../utils';
import Button from '../../../generic/Button/Button';
import NavigationButton from '../../../generic/Button/NavigationButton';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { icons, IconText } from '../../../generic/Icon';
import { DOCS_URL } from '../../../generic/Link';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import type { CargoRegistry } from '../../../../../typings/common';

import Registry from './Registry';

/**
 * Rust (Cargo) settings page.
 */
const CargoSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettingsWithOverride,
    isLoading,
    isSaveButtonDisabled,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'cargo',
  );

  const registries = settings?.registries || [];

  const onAdd = () => {
    onUpdateSettings({
      registries: [...registries, { _id: nanoid(), _showTokenField: true }],
    });
  };

  const onRemove = (index: number) => {
    onUpdateSettings({
      registries: dropAt(registries, index),
    });
  };

  const onUpdateRegistry = <K extends keyof CargoRegistry>(
    index: number,
    member: K,
    value: CargoRegistry[K],
  ) => {
    onUpdateSettings({
      registries: registries.map((registry, i) => {
        if (i === index) {
          return {
            ...registry,
            [member]: value,
            hasToken: member === 'token' ? false : registry.hasToken,
            hasPassword: member === 'password' ? false : registry.hasPassword,
          };
        }
        return registry;
      }),
    });
  };

  const onSetShowTokenField = (index: number, value: string) => {
    onUpdateSettings({
      registries: registries.map((registry, i) => {
        if (i === index) {
          return { ...registry, _showTokenField: value === 'show-token-field' };
        }
        return registry;
      }),
    });
  };

  const onSaveSettings = () => {
    const cleanedRegistries = registries.map((registry) => {
      const { username, password, hasPassword, token, hasToken, ...shared } = registry;
      if (registry._showTokenField) {
        return { ...shared, token, hasToken };
      }
      return { ...shared, username, password, hasPassword };
    });
    onUpdateSettings({ registries: cleanedRegistries });
    onSaveSettingsWithOverride({ registries: cleanedRegistries });
  };

  const isFreeOrg = isOrgInFreeTier();

  return (
    <PaidFeatureGate isRestricted={isFreeOrg} hidePill>
      <Header heading="Rust Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="Registries">
              <Section.Paragraph>
                If you use a private Cargo registry, add it here so FOSSA can access your packages during analysis.
              </Section.Paragraph>
              <NavigationButton
                to={`${DOCS_URL}/rust`}
                size="medium"
                type="secondary"
                openInNewTab
              >
                View documentation
              </NavigationButton>
            </Section.Left>
            <Section.Right>
              {registries.map((registry, index) =>
                <Registry
                  key={registry._id}
                  registry={registry}
                  onRemove={() => onRemove(index)}
                  onUpdateRegistry={
                    <K extends keyof CargoRegistry>(
                      member: K,
                      value: CargoRegistry[K],
                    ) => onUpdateRegistry(index, member, value)
                  }
                  onSetShowTokenField={value => onSetShowTokenField(index, value)}
                />,
              )}
              <Button
                type="secondary"
                size="medium"
                onClick={onAdd}
              >
                <IconText icon={icons.plus} text="Add registry" />
              </Button>
            </Section.Right>
          </Section>
          )}
    </PaidFeatureGate>
  );
};

export default CargoSettings;
